/*
    Copyright 2024 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { inspect } from "util";
import { HttpResponseInit, PostInvocationContext, PreInvocationContext, app } from "@azure/functions";
import { addErrorAttributes, configureDynatrace as coreConfigureDynatrace, SemConvTrace, setPropagatedResourceAttributes } from "@dynatrace/opentelemetry-core";
import { trace, SpanKind, context as otelContext, Span, SpanStatusCode, SpanAttributes } from "@opentelemetry/api";
import type { NodeTracerProvider } from "@opentelemetry/sdk-trace-node";

import { getStartAttributes } from "./AttributeDetector";
import { logger } from "./Logger";
import { detectResource } from "./ResourceDetector";
import { extractContext, getTracer, isAzureHttpRequest } from "./Util";

// ============================================================================

const cDtSpanSymbol = Symbol("Dt Span");
interface DtHookData {
	[cDtSpanSymbol]: Span;
}

let endHookRegistered = false;
// only exported for testing
export function _testClearEndHookRegistered(): void {
	endHookRegistered = false;
}


// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-deprecated -- Api 1.0.0 compat
type Attributes = SpanAttributes;

// ============================================================================

/**
 * Creates and registers TracerProvider with all required components to export traces to Dynatrace.
 *
 * @param resource Resource passed to TracerProvider.
 *
 * @returns registered NodeTracerProvider
 */
export const configureDynatrace = function(resource: Attributes = {}): NodeTracerProvider {
	if (logger.debugEnabled) {
		logger.debug(`Configuring dynatrace. Resource: ${inspect(resource)}`);
	}
	return coreConfigureDynatrace(resource);
};

// ============================================================================
/**
 * Extracts trigger type and input from the provided context
 * @param context PreInvocationContext containing trigger info
 *
 * @returns extracted trigger (FaasTriggerValues) and input
 */
function getTriggerData(context: PreInvocationContext): { triggerType: SemConvTrace.FaasTriggerValues; req: unknown } {
	if (context.invocationContext.options.trigger.type === "httpTrigger") {
		return { triggerType: SemConvTrace.FaasTriggerValues.HTTP, req: context.inputs[0] };
	} else {
		return { triggerType: SemConvTrace.FaasTriggerValues.OTHER, req: undefined };
	}
}

// ============================================================================
/**
 * Returns status code of the Azure Function.
 *
 * If status is not set it defaults to 200:
 * https://learn.microsoft.com/en-us/azure/azure-functions/functions-reference-node?tabs=javascript%2Cwindows%2Cazure-cli&pivots=nodejs-model-v4#http-response
 *
 * If no response object is given status defaults to 204.
 *
 * @param context PreInvocationContext containing result of the Azure Function execution
 *
 * @returns Http status code returned from the Azure Function
 */
function getStatus(context: PostInvocationContext): number {
	let status: number;
	const res = context.result as HttpResponseInit;
	if (res != null) {
		status = res.status != null ? +res.status : 200;
	} else {
		status = 204;
	}

	if (logger.debugEnabled) {
		logger.debug(`${SemConvTrace.HTTP_STATUS_CODE}: ${status}`);
	}
	return status;
}

// ============================================================================
/**
 * Registers AzureFunction preInvocation hook for span start
 *
 * Use with Azure Function Programming Model V4 only.
 */
export function registerTraceStartHook(): void {
	if (logger.debugEnabled) {
		logger.debug("registerTraceStartHook");
	}

	app.hook.preInvocation((context: PreInvocationContext) => {
		if (logger.debugEnabled) {
			logger.debug(`Entered preInvocation hook for function ${context.invocationContext.functionName}. Trigger type: ${context.invocationContext.options.trigger.type}.`);
		}

		if (!endHookRegistered) {
			logger.info("TraceEndHook not rigestered, skip starting the trace.");
			return;
		}

		const resourceAttributes = detectResource(context.invocationContext.functionName);
		const { triggerType, req } = getTriggerData(context);
		const azureHttpRequest = isAzureHttpRequest(req) ? req : undefined;
		const parentCtx = extractContext(triggerType, azureHttpRequest);
		const span = getTracer().startSpan(
			context.invocationContext.functionName ?? "invoke",
			{
				kind: SpanKind.SERVER,
				attributes: {
					...resourceAttributes,
					...getStartAttributes(triggerType, azureHttpRequest)
				}
			},
			parentCtx);

		setPropagatedResourceAttributes(span, resourceAttributes);

		context.functionHandler = otelContext.bind(trace.setSpan(parentCtx, span), context.functionHandler);

		(context.hookData as unknown as DtHookData)[cDtSpanSymbol] = span;
	});
}

// ============================================================================
/**
 * Registers AzureFunction postInvocation hook for span end.
 *
 * Use with Azure Function Programming Model V4 only.
 */
export function registerTraceEndHook(): void {
	if (logger.debugEnabled) {
		logger.debug("registerTraceEndHook");
	}

	app.hook.postInvocation((context: PostInvocationContext) => {
		if (logger.debugEnabled) {
			logger.debug(`Entered postInvocation hook for function ${context.invocationContext.functionName}. Trigger type: ${context.invocationContext.options.trigger.type}`);
		}

		const span = (context.hookData as unknown as DtHookData)[cDtSpanSymbol];

		if (span != null) {
			if (context.invocationContext.options.trigger.type === "httpTrigger") {
				const status = getStatus(context);
				span.setAttribute(SemConvTrace.HTTP_STATUS_CODE, status);
			}

			if (context.error != null) {
				addErrorAttributes(span, context.error);
				span.setStatus({ code: SpanStatusCode.ERROR });
			}

			span.end();
		}
	});

	endHookRegistered = true;
}

// ============================================================================
/**
 * It registers AzureFunctions TraceStartHook and TraceEndHook to start and end function span.
 * Optionally, configures OpenTelemetry tracing components and registers a TracerProvider.
 *
 * Use with Azure Function Programming Model V4 only.
 *
 * @note In case other hooks need to be registered in the same AzureFunctions app,
 * consider using registerTraceStartHook, registerTraceEndHook to order them properly.
 * To properly trace monitored function the TraceStartHook hook must be the first preInvocation hook in the app.
 * Correspondingly the TraceEndHook must be the last postInvocation hook in the app.
 *
 * @param configure If set to true, configures required OTel components for tracing and returns registered NodeTracerProvider.
 * Otherwise, it doesn't return a tracer provider and the components must be configured manually or by calling configureDynatrace(). Default: false.
 *
 * @param resource Resource passed to TracerProvider.
 */
export function initDynatrace(configure = false, resource: Attributes = {}): void | NodeTracerProvider {
	registerTraceStartHook();

	registerTraceEndHook();

	if (configure) {
		return configureDynatrace(resource);
	}
}
